import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, ShieldCheck, Users } from "lucide-react";
import { HERO } from "@/constants/testIds";

const stats = [
  { id: "lanes", value: "200+", label: "Lanes across Southern Africa" },
  { id: "carriers", value: "1,200+", label: "Compliant carriers in our network" },
  { id: "loads", value: "60k+", label: "Loads brokered every year" },
  { id: "years", value: "15", label: "Years building trusted relationships" },
];

const heroImg =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Hero() {
  return (
    <section
      id="top"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 bg-grid mask-radial" />
      <div className="absolute -top-40 -right-40 -z-10 h-[520px] w-[520px] rounded-full bg-[#00AEEF]/15 blur-3xl" />
      <div className="absolute -top-20 -left-32 -z-10 h-[420px] w-[420px] rounded-full bg-[#0070C0]/15 blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid={HERO.badge}
              className="inline-flex items-center gap-2 rounded-full border border-[#E2E8F0] bg-white px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0070C0] shadow-sm"
            >
              <MapPin className="h-3.5 w-3.5" />
              Freight brokers · Southern Africa
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              data-testid={HERO.title}
              className="font-display mt-6 text-[44px] sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tighter text-[#002050]"
            >
              Where shippers and carriers
              <span className="block">
                {" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-[#0070C0] via-[#00AEEF] to-[#0070C0] bg-clip-text text-transparent">
                    connect.
                  </span>
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    viewBox="0 0 300 12"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 8 C 80 2, 200 2, 298 8"
                      stroke="#00AEEF"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12 }}
              data-testid={HERO.subtitle}
              className="mt-6 max-w-2xl text-lg md:text-xl text-[#1F2937]/80 leading-relaxed"
            >
              We're an independent freight brokerage moving loads across South
              Africa and the wider SADC region. One trusted point of contact,
              a vetted carrier network, and time-efficient distribution — so
              you can focus on your business.
            </motion.p>

            <motion.blockquote
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-7 max-w-xl border-l-4 border-[#00AEEF] pl-5 italic text-[15px] md:text-base text-[#1F2937]/75"
            >
              “Vehicles are one of the best modes of transportation.
              Relationships are one of the best vehicles of transformation.”
              <span className="block mt-2 not-italic text-sm font-semibold text-[#0070C0]">
                — Kate McGahan
              </span>
            </motion.blockquote>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.28 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href="#quote"
                data-testid={HERO.ctaDemo}
                className="group inline-flex items-center gap-2 bg-[#0070C0] hover:bg-[#002050] text-white px-7 py-4 rounded-xl text-[15px] font-bold transition-all duration-300 shadow-[0_8px_24px_rgba(0,112,192,0.35)] hover:shadow-[0_12px_30px_rgba(0,32,80,0.35)] hover:-translate-y-0.5"
              >
                Get a Quote
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#carriers"
                data-testid={HERO.ctaContact}
                className="group inline-flex items-center gap-2 bg-white text-[#002050] border-2 border-[#E2E8F0] hover:border-[#0070C0] px-7 py-4 rounded-xl text-[15px] font-bold transition-all duration-300"
              >
                <Users className="h-5 w-5 text-[#0070C0]" />
                Become a Carrier
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#1F2937]/70"
            >
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[#0070C0]" />
                NRTA · OHS Act · SARS compliant carriers
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#0070C0]" />
                South Africa · Botswana · Namibia · Zambia · Zimbabwe · Moz
              </div>
            </motion.div>
          </div>

          {/* Visual */}
          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,32,80,0.35)] border border-white/40"
            >
              <img
                src={heroImg}
                alt="Freight truck on the highway in Southern Africa"
                className="w-full h-[460px] md:h-[560px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#002050]/55 via-[#002050]/10 to-transparent" />

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute top-6 left-6 right-6 sm:right-auto sm:max-w-[280px] rounded-2xl bg-white/85 backdrop-blur-xl border border-white/60 shadow-[0_12px_40px_rgba(0,32,80,0.18)] p-4"
              >
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#0070C0] font-bold">
                  One Point of Contact
                </div>
                <div className="mt-2 font-display text-[#002050] font-bold text-lg leading-tight">
                  From shipper to carrier — we handle it all.
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <div className="h-8 w-8 rounded-full bg-[#0070C0] border-2 border-white" />
                    <div className="h-8 w-8 rounded-full bg-[#00AEEF] border-2 border-white" />
                    <div className="h-8 w-8 rounded-full bg-[#002050] border-2 border-white" />
                  </div>
                  <div className="text-xs text-[#1F2937]/70">
                    Shipper · Broker · Carrier
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.55 }}
                className="absolute bottom-6 right-6 left-6 sm:left-auto sm:max-w-[260px] rounded-2xl bg-[#002050] text-white p-4 shadow-[0_12px_40px_rgba(0,32,80,0.4)]"
              >
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#00AEEF] font-bold">
                  <MapPin className="h-3.5 w-3.5" /> Live capacity
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <div>
                    <div className="font-display text-2xl font-extrabold">
                      JHB
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/60">
                      Gauteng
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-extrabold text-[#00AEEF]">
                      CPT
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/60">
                      W. Cape
                    </div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-extrabold">
                      DBN
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/60">
                      KZN
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <div className="absolute -z-10 -bottom-10 -right-10 h-60 w-60 rounded-full bg-[#00AEEF]/30 blur-3xl" />
          </div>
        </div>

        {/* Stats */}
        <div className="mt-20 md:mt-28">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-2xl overflow-hidden bg-[#E2E8F0] border border-[#E2E8F0]">
            {stats.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={HERO.statsCard(s.id)}
                className="bg-white p-8 md:p-10 flex flex-col gap-1.5 group hover:bg-[#F8FAFC] transition-colors"
              >
                <div className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter text-[#002050]">
                  {s.value}
                </div>
                <div className="text-sm md:text-[15px] text-[#1F2937]/70">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
